import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import ContentNetworkLayer from '../../helpers';
import { subjectUsesMath } from '../EquationModal';
import RichEditor from '../RichEditor';
import LaTeXText from '../LaTeXText';

const SUBJECT_COLORS = [
  '#00ADB5',
  '#E91E9C',
  '#8B4563',
  '#2C3E50',
  '#F5A623',
  '#1ABC9C',
  '#B19CD9',
  '#3498DB',
  '#E74C3C',
  '#9B59B6',
];

const TYPE_LABELS = {
  MCQ: 'Multiple Choice',
  'True/False': 'True / False',
  FIB: 'Fill In Blanks',
  Matching: 'Matching / Order',
  MatchingPairs: 'Matching Pairs',
  MultiSelect: 'Multi-Select',
  SwipeLeftRight: 'Swipe Left/Right',
  DragDrop: 'Drag and Drop',
};

const TYPE_COLORS = {
  MCQ: '#3498DB',
  'True/False': '#27AE60',
  FIB: '#E67E22',
  Matching: '#9B59B6',
  MatchingPairs: '#9B59B6',
  MultiSelect: '#E91E63',
  SwipeLeftRight: '#F39C12',
  DragDrop: '#00BCD4',
};

/* ── Delete Confirmation Modal ──────────────────────────────── */
function DeleteConfirmModal({ isOpen, onClose, onConfirm, title, message, loading }) {
  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#ef4444' }}>
            Delete {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              width: 28,
              height: 28,
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <p style={{ color: '#475569', marginBottom: 24, lineHeight: 1.6, fontSize: '0.9rem' }}>
            {message}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '9px 20px',
                borderRadius: 8,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: '#f1f5f9',
                color: '#475569',
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              style={{
                padding: '9px 20px',
                borderRadius: 8,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: '1.5px solid #fca5a5',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#fee2e2' : '#fff',
                color: '#ef4444',
              }}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const COMPLEXITY_COLORS = { Easy: '#16a34a', Intermediate: '#f59e0b', Advance: '#ef4444' };

/* ── Question Card Component ──────────────────────────────── */
function QuestionCard({ question, onDeleted, onUpdated, showEquationButton = false }) {
  const [editing, setEditing] = useState(false);
  // Support both v2 (snake_case and camelCase) and v1 field names
  const questionText = question.questionContent ?? question['question_content'] ?? question.questionText ?? '';
  const [draftQuestion, setDraftQuestion] = useState(questionText);
  const [draftAnswers, setDraftAnswers] = useState([]);
  const [draftComplexity, setDraftComplexity] = useState(question.complexity || 'Easy');
  const [draftTrueFalse, setDraftTrueFalse] = useState(true); // Separate state for True/False
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Initialize draft answers based on question type - only when question changes
  useEffect(() => {
    // Update draft question text when question changes
    setDraftQuestion(question.questionContent ?? question['question_content'] ?? question.questionText ?? '');
    
    if (question.type === 'True/False') {
      // For True/False, check if "True" answer is correct
      const trueAnswer = question.multipleChoiceAnswers?.find(a => {
        const answerText = a.answerContent ?? a['answer_content'] ?? a.answer ?? '';
        return answerText.toLowerCase() === 'true' || answerText.toLowerCase().includes('true');
      });
      setDraftTrueFalse(trueAnswer?.isCorrect === 1);
    } else if (
      question.type === 'MCQ' ||
      question.type === 'MultiSelect'
    ) {
      setDraftAnswers(
        question.multipleChoiceAnswers?.map(a => ({
          answeredId: a.answeredId,
          answer: a.answerContent ?? a['answer_content'] ?? a.answer ?? '',
          isCorrect: a.isCorrect === 1,
        })) || []
      );
    } else if (question.type === 'Matching' || question.type === 'MatchingPairs') {
      setDraftAnswers(
        question.matchingAnswers?.map(a => ({
          answeredId: a.answeredId,
          answer: a.answerContent ?? a['answer_content'] ?? a.answer ?? '',
          order: a.order,
        })) || []
      );
    } else if (question.type === 'FIB') {
      setDraftAnswers(
        question.multipleChoiceAnswers?.map(a => ({
          answeredId: a.answeredId,
          answer: a.answerContent ?? a['answer_content'] ?? a.answer ?? '',
          isCorrect: a.isCorrect === 1,
        })) || []
      );
    }
  }, [question]); // Removed 'editing' from dependencies - don't reset when editing state changes

  const handleEdit = () => {
    setDraftQuestion(question.questionContent ?? question.questionText ?? '');
    setDraftComplexity(question.complexity || 'Easy');
    setEditing(true);
  };

  const handleCancel = () => {
    setDraftQuestion(question.questionText || '');
    setEditing(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let answersPayload;
      
      if (question.type === 'True/False') {
        // Build True/False answer format using draftTrueFalse state
        answersPayload = [
          { answer: 'True', is_correct: draftTrueFalse ? 1 : 0 },
          { answer: 'False', is_correct: draftTrueFalse ? 0 : 1 },
        ];
      } else if (question.type === 'Matching' || question.type === 'MatchingPairs') {
        answersPayload = draftAnswers.map(a => ({
          answer: a.answer,
          order: a.order,
        }));
      } else {
        // MCQ, MultiSelect, FIB
        answersPayload = draftAnswers.map(a => ({
          answer: a.answer,
          is_correct: a.isCorrect ? 1 : 0,
        }));
      }
      
      await ContentNetworkLayer.updateQuestion(question.questionId, {
        questionText: draftQuestion,
        complexity: draftComplexity,
        answers: answersPayload,
      });
      toast.success('Question updated!');
      if (onUpdated) {
        // Patch v2 (questionContent) and v1 (questionText) shapes so the read
        // effect doesn't snap the editor back to the stale server value.
        // Also rebuild the answer arrays the read effect consumes
        // (multipleChoiceAnswers / matchingAnswers) in their full shape.
        const isMatching = question.type === 'Matching' || question.type === 'MatchingPairs';
        let patchedMcq = question.multipleChoiceAnswers;
        let patchedMatching = question.matchingAnswers;

        if (question.type === 'True/False') {
          patchedMcq = [
            { answeredId: question.multipleChoiceAnswers?.[0]?.answeredId, answerContent: 'True', answer_content: 'True', answer: 'True', isCorrect: draftTrueFalse ? 1 : 0 },
            { answeredId: question.multipleChoiceAnswers?.[1]?.answeredId, answerContent: 'False', answer_content: 'False', answer: 'False', isCorrect: draftTrueFalse ? 0 : 1 },
          ];
        } else if (isMatching) {
          patchedMatching = draftAnswers.map(a => ({
            answeredId: a.answeredId,
            answerContent: a.answer,
            answer_content: a.answer,
            answer: a.answer,
            order: a.order,
          }));
        } else {
          // MCQ, MultiSelect, FIB
          patchedMcq = draftAnswers.map(a => ({
            answeredId: a.answeredId,
            answerContent: a.answer,
            answer_content: a.answer,
            answer: a.answer,
            isCorrect: a.isCorrect ? 1 : 0,
          }));
        }

        onUpdated({
          ...question,
          questionContent: draftQuestion,
          question_content: draftQuestion,
          questionText: draftQuestion,
          complexity: draftComplexity,
          multipleChoiceAnswers: patchedMcq,
          matchingAnswers: patchedMatching,
        });
      }
      setEditing(false);
    } catch (err) {
      toast.error('Failed to update question.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteQuestion(question.questionId);
      toast.success('Question deleted!');
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted(question.questionId);
    } catch {
      toast.error('Failed to delete question.');
    } finally {
      setDeleting(false);
    }
  };

  const updateAnswer = (index, field, value) => {
    const updated = [...draftAnswers];
    updated[index] = { ...updated[index], [field]: value };
    setDraftAnswers(updated);
  };

  const toggleCorrect = index => {
    const updated = [...draftAnswers];
    if (question.type === 'MCQ') {
      // Only one correct for MCQ
      updated.forEach((a, i) => (a.isCorrect = i === index));
    } else {
      // Multiple correct for MultiSelect
      updated[index].isCorrect = !updated[index].isCorrect;
    }
    setDraftAnswers(updated);
  };

  const renderAnswers = () => {
    if (question.type === 'MCQ' || question.type === 'MultiSelect') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(editing ? draftAnswers : question.multipleChoiceAnswers || []).map((answer, idx) => (
            <div
              key={answer.answeredId || idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                background: (editing ? answer.isCorrect : answer.isCorrect === 1)
                  ? '#dcfce7'
                  : '#f8fafc',
                borderRadius: 6,
                border: `1px solid ${(editing ? answer.isCorrect : answer.isCorrect === 1) ? '#86efac' : '#e2e8f0'}`,
              }}
            >
              {editing ? (
                <>
                  <input
                    type={question.type === 'MCQ' ? 'radio' : 'checkbox'}
                    checked={answer.isCorrect}
                    onChange={() => toggleCorrect(idx)}
                    style={{ cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={answer.answer}
                    onChange={e => updateAnswer(idx, 'answer', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      fontSize: '0.85rem',
                    }}
                  />
                </>
              ) : (
                <>
                  <span style={{ color: answer.isCorrect === 1 ? '#16a34a' : '#64748b' }}>
                    {answer.isCorrect === 1 ? '✓' : '○'}
                  </span>
                  <LaTeXText style={{ fontSize: '0.85rem', color: '#1a202c' }}>
                    {answer.answer}
                  </LaTeXText>
                </>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (question.type === 'True/False') {
      if (editing) {
        return (
          <div style={{ display: 'flex', gap: 12 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: draftTrueFalse ? '#dcfce7' : '#f8fafc',
                borderRadius: 6,
                border: `2px solid ${draftTrueFalse ? '#16a34a' : '#e2e8f0'}`,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: draftTrueFalse ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={`tf-${question.questionId}`}
                checked={draftTrueFalse}
                onChange={() => setDraftTrueFalse(true)}
                style={{ cursor: 'pointer' }}
              />
              True
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: !draftTrueFalse ? '#dcfce7' : '#f8fafc',
                borderRadius: 6,
                border: `2px solid ${!draftTrueFalse ? '#16a34a' : '#e2e8f0'}`,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: !draftTrueFalse ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={`tf-${question.questionId}`}
                checked={!draftTrueFalse}
                onChange={() => setDraftTrueFalse(false)}
                style={{ cursor: 'pointer' }}
              />
              False
            </label>
          </div>
        );
      }
      
      // View mode
      const viewIsTrue = question.multipleChoiceAnswers?.find(a => 
        a.answer?.toLowerCase() === 'true' || a.answer?.toLowerCase().includes('true')
      )?.isCorrect === 1;
      
      return (
        <div style={{ display: 'flex', gap: 12 }}>
          <div
            style={{
              padding: '8px 16px',
              background: viewIsTrue ? '#dcfce7' : '#f8fafc',
              borderRadius: 6,
              border: `1px solid ${viewIsTrue ? '#86efac' : '#e2e8f0'}`,
              fontSize: '0.85rem',
            }}
          >
            {viewIsTrue && '✓ '}True
          </div>
          <div
            style={{
              padding: '8px 16px',
              background: !viewIsTrue ? '#dcfce7' : '#f8fafc',
              borderRadius: 6,
              border: `1px solid ${!viewIsTrue ? '#86efac' : '#e2e8f0'}`,
              fontSize: '0.85rem',
            }}
          >
            {!viewIsTrue && '✓ '}False
          </div>
        </div>
      );
    }

    if (question.type === 'Matching' || question.type === 'MatchingPairs') {
      const answers = editing ? draftAnswers : (question.matchingAnswers || []);
      
      // For MatchingPairs: first half are terms, second half are definitions
      // Group by order to show as pairs
      if (question.type === 'MatchingPairs' && answers.length > 0) {
        const half = Math.floor(answers.length / 2);
        const terms = answers.slice(0, half);
        const definitions = answers.slice(half);
        
        // Create pairs by matching order values
        const pairs = terms.map((term, termIdx) => {
          const defIdx = definitions.findIndex(d => d.order === term.order);
          const definition = defIdx >= 0 ? definitions[defIdx] : null;
          return { 
            term, 
            definition, 
            termIdx,  // Index in draftAnswers for term
            defIdx: defIdx >= 0 ? half + defIdx : -1  // Index in draftAnswers for definition
          };
        });
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Header row */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              paddingBottom: 8,
              borderBottom: '1px solid #e2e8f0',
              marginBottom: 4,
            }}>
              <span style={{ width: 26 }}></span>
              <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Term
              </span>
              <span style={{ width: 24 }}></span>
              <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Definition
              </span>
            </div>
            
            {pairs.map((pair, idx) => (
              <div
                key={pair.term?.answeredId || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                }}
              >
                {/* Order badge */}
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: '#00ADB5',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {pair.term?.order}
                </span>
                
                {editing ? (
                  <>
                    {/* Term input */}
                    <input
                      type="text"
                      value={pair.term?.answer || ''}
                      onChange={e => updateAnswer(pair.termIdx, 'answer', e.target.value)}
                      placeholder="Term"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                      }}
                    />
                    {/* Arrow */}
                    <span style={{ color: '#94a3b8', fontSize: '1.2rem', flexShrink: 0 }}>→</span>
                    {/* Definition input */}
                    <input
                      type="text"
                      value={pair.definition?.answer || ''}
                      onChange={e => updateAnswer(pair.defIdx, 'answer', e.target.value)}
                      placeholder="Definition"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* Term */}
                    <LaTeXText style={{ 
                      fontSize: '0.85rem', 
                      color: '#1a202c', 
                      fontWeight: 600,
                      flex: 1,
                    }}>
                      {pair.term?.answer}
                    </LaTeXText>
                    {/* Arrow */}
                    <span style={{ color: '#94a3b8', fontSize: '1rem', flexShrink: 0 }}>→</span>
                    {/* Definition */}
                    <LaTeXText style={{ 
                      fontSize: '0.85rem', 
                      color: '#64748b',
                      flex: 1,
                    }}>
                      {pair.definition?.answer}
                    </LaTeXText>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      }
      
      // Fallback for legacy Matching type (flat list)
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {answers
            .sort((a, b) => a.order - b.order)
            .map((answer, idx) => (
              <div
                key={answer.answeredId || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  background: '#f8fafc',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#00ADB5',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {answer.order}
                </span>
                {editing ? (
                  <input
                    type="text"
                    value={answer.answer}
                    onChange={e => updateAnswer(idx, 'answer', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      fontSize: '0.85rem',
                    }}
                  />
                ) : (
                  <LaTeXText style={{ fontSize: '0.85rem', color: '#1a202c' }}>
                    {answer.answer}
                  </LaTeXText>
                )}
              </div>
            ))}
        </div>
      );
    }

    if (question.type === 'FIB') {
      const correctAnswers = question.multipleChoiceAnswers?.filter(a => a.isCorrect === 1) || [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {correctAnswers.map((answer, idx) => (
            <div
              key={answer.answeredId || idx}
              style={{
                padding: '6px 12px',
                background: '#dcfce7',
                borderRadius: 6,
                border: '1px solid #86efac',
                fontSize: '0.85rem',
                color: '#16a34a',
              }}
            >
              <LaTeXText style={{ color: '#16a34a' }}>
                {answer.answer}
              </LaTeXText>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: 16,
        marginBottom: 12,
      }}
    >
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Question"
        message={`Are you sure you want to delete this question? This will also delete all associated answers. This action cannot be undone.`}
        loading={deleting}
      />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: 4,
              background: `${TYPE_COLORS[question.type] || '#64748b'}20`,
              color: TYPE_COLORS[question.type] || '#64748b',
            }}
          >
            {TYPE_LABELS[question.type] || question.type}
          </span>
          {!editing ? (
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 4,
                background: `${COMPLEXITY_COLORS[question.complexity] || COMPLEXITY_COLORS.Easy}20`,
                color: COMPLEXITY_COLORS[question.complexity] || COMPLEXITY_COLORS.Easy,
              }}
            >
              {question.complexity || 'Easy'}
            </span>
          ) : (
            <div className="complexity-selector complexity-selector--mini">
              {['Easy', 'Intermediate', 'Advance'].map(level => (
                <button
                  key={level}
                  type="button"
                  className={`complexity-btn complexity-btn--${level.toLowerCase()} ${
                    draftComplexity === level ? 'active' : ''
                  }`}
                  onClick={() => setDraftComplexity(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          )}
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>ID: {question.questionId}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleEdit}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-danger-ghost btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Question Text */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Question</div>
        {editing ? (
          <RichEditor
            value={draftQuestion}
            onChange={setDraftQuestion}
            placeholder="Question text..."
            minHeight={80}
            showEquationButton={showEquationButton}
          />
        ) : (
          <LaTeXText style={{ fontSize: '0.9rem', color: '#1a202c', lineHeight: 1.5 }}>
            {questionText}
          </LaTeXText>
        )}
      </div>

      {/* Answers */}
      <div>
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>Answers</div>
        {renderAnswers()}
      </div>

      {/* Edit actions */}
      {editing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main BrowseQuestions Component ──────────────────────────────── */
export default function BrowseQuestions() {
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');

  // Load subjects on mount
  useEffect(() => {
    ContentNetworkLayer.getSubjects()
      .then(res => setSubjects(res.subjects || []))
      .catch(() => toast.error('Could not load subjects.'))
      .finally(() => setLoading(false));
  }, []);

  // Load questions when subject is selected
  useEffect(() => {
    if (!selectedSubject) {
      setQuestions([]);
      return;
    }
    setLoadingQuestions(true);
    ContentNetworkLayer.getQuestions()
      .then(res => {
        const filtered = (res || []).filter(q => q.code === selectedSubject.code);
        setQuestions(filtered);
      })
      .catch(() => toast.error('Could not load questions.'))
      .finally(() => setLoadingQuestions(false));
  }, [selectedSubject]);

  // Check if selected subject uses math
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Filter questions by search and type
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      const matchesSearch =
        !searchTerm || q.questionText?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !filterType || q.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [questions, searchTerm, filterType]);

  // Group questions by type for stats
  const questionStats = useMemo(() => {
    const stats = {};
    questions.forEach(q => {
      stats[q.type] = (stats[q.type] || 0) + 1;
    });
    return stats;
  }, [questions]);

  const handleQuestionDeleted = questionId => {
    setQuestions(prev => prev.filter(q => q.questionId !== questionId));
  };

  const handleQuestionUpdated = updatedQuestion => {
    setQuestions(prev =>
      prev.map(q =>
        q.questionId === updatedQuestion.questionId ? { ...q, ...updatedQuestion } : q
      )
    );
  };

  const subjectColor = i => SUBJECT_COLORS[i % SUBJECT_COLORS.length];

  if (loading) {
    return (
      <div className="form-card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* ── Subject sidebar ─────────────────────────────────── */}
      <div className="form-card" style={{ width: 260, flexShrink: 0 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a202c' }}>Subjects</div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
            Select to view questions
          </div>
        </div>

        {subjects.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: 16 }}>
            No subjects yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {subjects.map((s, i) => {
              const isSelected = selectedSubject?.code === s.code;
              return (
                <button
                  key={s.code}
                  onClick={() => {
                    setSelectedSubject(isSelected ? null : s);
                    setSearchTerm('');
                    setFilterType('');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    border: `1.5px solid ${isSelected ? subjectColor(i) : '#e2e8f0'}`,
                    borderRadius: 8,
                    background: isSelected ? `${subjectColor(i)}14` : '#f8fafc',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: subjectColor(i),
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        color: '#1a202c',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.name}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{s.level}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Questions panel ─────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedSubject ? (
          <div className="form-card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>❓</div>
            <div style={{ fontWeight: 600, color: '#1a202c', marginBottom: 4 }}>
              Select a subject
            </div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
              Click a subject to view and manage its questions.
            </div>
          </div>
        ) : (
          <div className="form-card">
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>
                {selectedSubject.name}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                {questions.length} question{questions.length !== 1 ? 's' : ''} total
              </div>
            </div>

            {/* Stats */}
            {questions.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {Object.entries(questionStats).map(([type, count]) => (
                  <div
                    key={type}
                    onClick={() => setFilterType(filterType === type ? '' : type)}
                    style={{
                      padding: '4px 10px',
                      background: filterType === type ? `${TYPE_COLORS[type]}20` : '#f1f5f9',
                      border: `1px solid ${filterType === type ? TYPE_COLORS[type] : '#e2e8f0'}`,
                      borderRadius: 20,
                      fontSize: '0.75rem',
                      color: filterType === type ? TYPE_COLORS[type] : '#64748b',
                      cursor: 'pointer',
                      fontWeight: filterType === type ? 600 : 400,
                    }}
                  >
                    {TYPE_LABELS[type] || type}: {count}
                  </div>
                ))}
              </div>
            )}

            {/* Search */}
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                }}
              />
            </div>

            {/* Questions list */}
            {loadingQuestions ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                Loading questions...
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div
                style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: '0.85rem' }}
              >
                {questions.length === 0
                  ? 'No questions yet for this subject.'
                  : 'No questions match your filters.'}
              </div>
            ) : (
              <div>
                {filteredQuestions.map(question => (
                  <QuestionCard
                    key={question.questionId}
                    question={question}
                    onDeleted={handleQuestionDeleted}
                    onUpdated={handleQuestionUpdated}
                    showEquationButton={showEquationButton}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
